import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  TRACKING_ACTIONS,
  TRACKING_ELEMENT_TYPES,
  trackCustomEvent,
  trackEvent,
} from '@medflex/utils/Tracking';
import PropTypes from 'prop-types';
import { CLIENT_TYPES, CHAT_MESSAGE_TYPE, SESSION_TYPES } from '@medflex/utils/constants';
import { IconCog, IconTrash } from '@medflex/components/stories/Icons';
import ChatStream from '@medflex/components/stories/ChatStream';
import Spinner from '@medflex/components/stories/Spinner';
import { useTranslation } from 'react-i18next';
import Button from '@medflex/components/stories/Button';
import getChatStreamContentAsString from '@medflex/utils/getChatStreamContentAsString';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import format from '@medflex/i18n/lib/format';
import config from '@medflex/utils/config';
import SessionChatStreamHeader from '@medflex/components/stories/SessionChatStreamHeader';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import useUserSession from '@medflex/hooks/useUserSession';
import isTeamChatId from '@medflex/utils/isTeamChatId';
import useIsFocused from '@medflex/hooks/useIsFocused';
import InactiveChatRemark from '@medflex/components/stories/InactiveChatRemark';
import isPatientsOnlySession from '@medflex/utils/isPatientsOnlySession';
import { FEATURE_PROP_TYPE } from '@medflex/utils/subscriptionEnums';
import { downloadMp3, download, open as openFile } from '@medflex/utils/handleFile';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import { MessageTypes, PhoneStatus, RequestStatus, Sources } from '@medflex/shared-types';
import isSessionPMChatId from '@medflex/utils/isSessionPMChatId';
import InquiryStatusLabel, {
  InquiryStatusLabelVariants,
} from '@medflex/components/stories/PamInquiry/InquiryStatusLabel';
import { PhoneStatusBadge } from '@medflex/components/stories/PamInquiry';
import { Variants } from '@medflex/components/stories/PamInquiry/PhoneStatusBadge';
import PhysicianStatusBar from '@medflex/components/stories/PhysicianStatusBar';
import { getFullNameWithFL, isAbsenceActive } from '@medflex/shared-utils';
import { DocumentIcon } from '@heroicons/react/24/solid';
import AbsenceInfoBanner from '@medflex/components/stories/AbsenceInfoBanner';
import { useLocation } from 'react-router-dom';
import getInitials from '@medflex/utils/getInitials';
import useSessionChat from '../../hooks/useSessionChat';
import { MODALS } from '../../hooks/useIsModal';
import useModal from '../../hooks/useModal';
import SessionChatMessageContextMenu from './SessionChatMessageContextMenu';
import useUserProfile from '../../hooks/useUserProfile';
import useSnippets from '../../hooks/useSnippets';
import useChatContacts from '../../hooks/useChatContacts';
import useSessionRoles from '../../hooks/useSessionRoles';
import ComposerAreaModule from '../ComposerAreaModule';
import ChatTitleBarModule from '../ChatTitleBarModule';
import getInquiryTitle from '../../../utils/getInquiryTitle';
import SessionChatModuleQuickReply, { MODE } from './SessionChatModuleQuickReply';
import RequestInfoBanner from './PamSession/RequestInfoBanner';
import useClipboard from '../../hooks/useClipboard';
import PatientWidgetAction from '../ComposerAreaModule/PatientWidgetActions';
import usePhysicianStatus from '../../hooks/usePhysicianStatus';
import useExternalServices, { EXTERNAL_SERVICES } from '../../hooks/useExternalServices';
import useGdt from '../../hooks/useGdt';

const SessionChatModule = ({ id, chatCharacters, participantId, tan }) => {
  const dropTarget = useRef();
  const quickReplyRef = useRef();
  const [quickReplyMode, setQuickReplyMode] = useState(MODE.FIXED);
  const [markedUnreadByUser, setMarkedUnreadByUser] = useState(false);
  const [selectedQuickReplySnippet, setSelectedQuickReplySnippet] = useState(null);
  const [manualOpenedMessageComposer, setManualOpenedMessageComposer] = useState(false);
  const { userProfile } = useUserProfile();
  const { session: userSession } = useUserSession();
  const { t } = useTranslation('messenger');
  const { getQueryString, open } = useModal();
  const focused = useIsFocused();
  const { findContactByUserId } = useChatContacts();
  const { snippets, quickReplySnippets } = useSnippets();
  const { messages, status, send, chat, setMessageImportant, setLastReadMessage } = useSessionChat({
    id,
    load: true,
    sessionParticipantId: participantId,
    tan,
  });
  const { getClipboard } = useClipboard();
  const clipboard = getClipboard();
  const hasClipboardText = clipboard[id + userProfile.id]?.length > 0;
  const [physicianStatusShowed, setPhysicianStatusShowed] = useState(false);
  const physicianUserId = chat?.physicianUserId;
  const { status: physicianStatusMap, toggleVisibility } = usePhysicianStatus(
    physicianUserId &&
      ![userProfile.id, userProfile.attributes.physicianUserId].includes(physicianUserId)
      ? [physicianUserId]
      : [],
  );
  const [simpleprax] = useExternalServices([EXTERNAL_SERVICES.SIMPLEPRAX]);
  const location = useLocation();

  const isPracticeToPatientChat =
    chat?.participants.filter(p => !p.physicianUserId).length >= 1 &&
    !chat.participants.some(p => p.physicianUserId && p.physicianUserId !== chat?.physicianUserId);

  const isPracticeToSinglePatientChat =
    chat?.participants.filter(p => !p.physicianUserId).length === 1 &&
    !chat.participants.some(p => p.physicianUserId && p.physicianUserId !== chat?.physicianUserId);

  const physicianStatus = isPracticeToPatientChat && physicianStatusMap?.[physicianUserId];

  const physicianUser = chat?.participants.find(p => p.userId === physicianUserId);

  const pvsId = useMemo(() => {
    if (!isPracticeToPatientChat || isPatientUserId(userProfile.id)) return null;

    if (chat?.reference?.data?.contactDetails?.representedPerson) {
      return findContactByUserId(chat?.reference?.data?.contactDetails?.representedPerson.userId)
        ?.pvsId;
    }

    return findContactByUserId(
      chat?.participants.find(p => p.userId && isPatientUserId(p.userId))?.userId,
    )?.pvsId;
  }, [chat, findContactByUserId, isPracticeToPatientChat, userProfile.id]);

  const { onWriteExamination, canWriteExamination } = useGdt(pvsId);

  useEffect(() => {
    if (physicianUserId && physicianStatus?.status && physicianStatus?.statusDisplayed) {
      setPhysicianStatusShowed(true);
    }

    return () => {
      if (physicianUserId && physicianStatus?.status && !physicianStatus?.statusDisplayed)
        return setPhysicianStatusShowed(false);

      return null;
    };
  }, [physicianUserId, physicianStatus]);

  useEffect(() => {
    if (
      !physicianStatusShowed &&
      physicianUserId &&
      physicianStatus?.status &&
      !physicianStatus?.statusDisplayed
    ) {
      toggleVisibility({ physicianUserId });
      open({ modal: MODALS.PHYSICIAN_STATUS_MODAL });
    }
  }, [physicianUserId, open, physicianStatus, toggleVisibility, physicianStatusShowed]);

  useEffect(() => {
    setManualOpenedMessageComposer(false);
  }, [chat?.reference?.data?.status, id]);

  const isPamSession = isSessionPMChatId(id);

  const [otherParticipants = [], userParticipant = null] = useMemo(() => {
    if (chat) {
      return chat.participants
        .map(p => ({
          ...p,
          initials: `${p.firstName[0]}${p.lastName[0]}`,
          fullNameWithTitle: getFullNameWithTitle(p),
        }))
        .reduce(
          (memo, p) => (p.userId === userProfile.id ? [memo[0], p] : [[...memo[0], p], memo[1]]),
          [[], null],
        );
    }

    return [];
  }, [chat, userProfile.id]);

  const hasInactiveUsers = useMemo(
    () => otherParticipants.some(p => !!p.deactivated),
    [otherParticipants],
  );

  const { isModerator, isOrganizer } = useSessionRoles(userParticipant ? [userParticipant] : []);

  const chatMessagesExport = useMemo(() => {
    if (messages && messages.length) {
      return getChatStreamContentAsString(messages, format);
    }
    return '';
  }, [messages]);

  const enhancedMessages = useMemo(() => {
    if (isPamSession && isPatientUserId(userProfile.id) && chat?.reference) {
      let senderData = {};
      if (chat.reference.data.source === Sources.PRACTICE) {
        senderData = {
          id: physicianUser.userId,
          firstName: physicianUser.firstName,
          lastName: physicianUser.lastName,
          fullName: getFullNameWithFL(physicianUser),
          fullNameWithTitle: getFullNameWithTitle(physicianUser),
          initials: getInitials({
            firstName: physicianUser.firstName,
            lastName: physicianUser.lastName,
          }),
          jobTitle: physicianUser.workArea1 || physicianUser.jobTitle,
        };
      } else {
        senderData = userProfile.attributes;
      }
      return [
        {
          data: chat.reference.data,
          id: chat.reference.id,
          isSender: chat.reference.data.source !== Sources.PRACTICE,
          isPatient: true,
          seen: !!chat.reference.data.readAt,
          sender: senderData,
          time: chat.reference.data.createdAt,
          type: CHAT_MESSAGE_TYPE.PAM_REQUEST,
          user: senderData,
        },
        ...messages,
      ];
    }
    return messages;
  }, [chat, isPamSession, messages, userProfile, physicianUser]);

  const toggleMessageImportant = ({ id: messageId, important }) => {
    setMessageImportant({ id: messageId, important: !important });
  };

  const handleMarkedAsUnread = ({ id: messageId }) => {
    setMarkedUnreadByUser(true);
    const index = messages.findIndex(m => m.id === messageId) - 1;
    if (index >= 0) {
      setLastReadMessage({ id: messages[index].id });
      return;
    }
    setLastReadMessage({ id: null });
  };

  const handleDeleteMessage = ({ id: messageId }) => {
    open({ modal: MODALS.deleteSessionChatMessage, messageId, chatId: chat.id });
  };

  const handleShowDetail = useCallback(
    event => {
      open({ modal: MODALS.sessionChatDetail, id });
      trackEvent(event);
    },
    [id, open],
  );

  const handleShowDeletionSettings = useCallback(
    event => {
      open({ modal: 'sessionChatDeleteSettings', id });
      trackEvent(event);
    },
    [id, open],
  );

  const handleLeave = useCallback(() => {
    open({ modal: MODALS.sessionChatLeave, id });
  }, [id, open]);

  const downloadFile = async (event, audioFile) => {
    const isSuccess = await downloadMp3({
      uri: `${config.PUBLIC_INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions/files/${audioFile.id}`,
      name: audioFile.name,
      token: userSession.access_token,
    });

    if (!isSuccess) {
      showAlertToast(t('global:ChatMessageStream.action_menu.download_failed'), null, {
        toastId: 'chatDownloadFailed',
      });
      return;
    }

    showSuccessToast(t('global:ChatMessageStream.action_menu.downloaded'), null, {
      toastId: 'chatDownloadSuccess',
    });

    const payload = {
      action: TRACKING_ACTIONS.DOWNLOAD,
      elementType: TRACKING_ELEMENT_TYPES.BUTTON,
      actionText: 'download',
    };
    trackEvent(event, payload);
  };

  async function* fetchAudioSource({ files: audioFiles }, messageType) {
    const { INSTANT_MESSAGE_SERVICE_ENDPOINT } = config;
    const audioFile = audioFiles[0];
    let rs;

    const options = {
      headers: {
        AUTHORIZATION: `Bearer ${userSession.access_token}`,
      },
    };

    if (messageType === MessageTypes.PAM) {
      rs = await fetch(`${config.PAM_API_URL}/files/${audioFile.id}`, options);

      const file = await rs.json();

      const downloadFile = await fetch(file.attributes.download.url);
      yield window.URL.createObjectURL(await downloadFile.blob());
      return;
    }

    rs = await fetch(
      `${INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions/files/${audioFile.id}/original`,
      options,
    );
    yield window.URL.createObjectURL(await rs.blob());

    rs = await fetch(
      `${INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions/files/${audioFile.id}/mp4`,
      options,
    );
    yield window.URL.createObjectURL(await rs.blob());
  }

  const title = useMemo(() => {
    if (chat) {
      if (chat.title) return chat.title;
      return otherParticipants.map(participant => participant.fullNameWithTitle).join(', ');
    }
    return '';
  }, [chat, otherParticipants]);

  useEffect(() => {
    if (status.loadMessages === 'succeeded') {
      const filteredMessages = messages.filter(({ type }) => type !== CHAT_MESSAGE_TYPE.SYSTEM);
      if (filteredMessages.length > 0 && !markedUnreadByUser && focused) {
        const lastMessageId = filteredMessages.pop().id;
        if (chat.messageLastReadId !== lastMessageId) {
          const timeout = setTimeout(async () => {
            await setLastReadMessage({ id: lastMessageId });
          }, 2000);

          return () => {
            clearTimeout(timeout);
          };
        }
      }
    }

    return undefined;
  }, [messages, status, markedUnreadByUser, focused, chat, setLastReadMessage]);

  const isInternal1On1 = useMemo(() => chat && chat.type === SESSION_TYPES.INTERNAL1ON1, [chat]);

  const titleActionItems = useMemo(
    () => [
      ...(isInternal1On1 || isPamSession
        ? []
        : [
            {
              text:
                isOrganizer || isModerator
                  ? t('SessionChatTitleBarContextMenu.organiseGroup')
                  : t('SessionChatTitleBarContextMenu.showParticipants'),
              onClick: handleShowDetail,
              icon: <IconCog size="text" />,
            },
            ...(isModerator || (chat && isTeamChatId(chat.id))
              ? []
              : [
                  {
                    text: t('SessionChatTitleBarContextMenu.leaveGroup'),
                    onClick: handleLeave,
                    icon: <IconTrash size="text" />,
                  },
                ]),
            ...((isOrganizer || isModerator) && !isTeamChatId(chat.id)
              ? [
                  {
                    text: t('SessionChatTitleBarContextMenu.deletionSettings'),
                    onClick: handleShowDeletionSettings,
                    icon: <IconTrash size="text" />,
                  },
                ]
              : []),
          ]),
    ],
    [
      isInternal1On1,
      isPamSession,
      isOrganizer,
      isModerator,
      t,
      handleShowDetail,
      chat,
      handleLeave,
      handleShowDeletionSettings,
    ],
  );

  const fileMessageClickHandler = useCallback(
    (file, messageType = null) => {
      openFile({
        uri:
          messageType === MessageTypes.PAM
            ? `${config.PAM_API_URL}/files/${file.id}`
            : `${config.PUBLIC_INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions/files/${file.id}`,
        token: userSession.access_token,
      });
    },
    [userSession.access_token],
  );

  const fileMessageDownloadHandler = useCallback(
    file => {
      download({
        uri: `${config.PAM_API_URL}/files/${file.id}`,
        token: userSession.access_token,
        name: file.name,
      });
    },
    [userSession.access_token],
  );

  const handleQuickReplyClick = useCallback(snippet => {
    setSelectedQuickReplySnippet(snippet);
  }, []);

  useEffect(() => {
    if (!quickReplyRef.current) return;

    const quickReplyWidth = Object.values(quickReplyRef.current.childNodes).reduce(
      (sum, child) => child.offsetWidth + sum,
      0,
    );
    const chatModuleWidth = dropTarget.current.offsetWidth;

    if (enhancedMessages.length && quickReplyWidth > chatModuleWidth) {
      setQuickReplyMode(MODE.CAROUSEL);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickReplyRef.current, enhancedMessages.length]);

  const handleOpenMessageComposer = useCallback(() => {
    setManualOpenedMessageComposer(true);
  }, []);

  const showMessageComposer = () => {
    if (
      isPracticeToPatientChat &&
      isAbsenceActive(physicianStatus?.absence) &&
      !physicianStatus?.absence.allowNotifications
    ) {
      return false;
    }
    if (!(isPamSession && isPatientUserId(userProfile.id))) {
      return true;
    }
    if (chat?.reference?.data?.status === RequestStatus.CLOSED) {
      if (manualOpenedMessageComposer || hasClipboardText) return true;
      return false;
    }
    if (chat?.reference?.data?.isForwardedCall) {
      return false;
    }
    return true;
  };

  const showInfoBannerBasedOnStatus =
    chat?.reference?.data?.status === RequestStatus.CLOSED ||
    [PhoneStatus.CANCELED, PhoneStatus.UNCOMPLETED].includes(chat?.reference?.data?.phoneStatus) ||
    chat?.reference?.data?.isForwardedCall;

  const showRequestInfoBanner =
    isPamSession &&
    isPatientUserId(userProfile.id) &&
    showInfoBannerBasedOnStatus &&
    !hasClipboardText &&
    !manualOpenedMessageComposer;

  const showPatientWidetActions =
    isPamSession &&
    isPatientUserId(userProfile.id) &&
    (chat?.reference?.data?.status === RequestStatus.CLOSED ||
      chat?.reference?.data?.isForwardedCall) &&
    !hasClipboardText &&
    !manualOpenedMessageComposer;

  const requestCanBeReopened = !(
    isAbsenceActive(physicianStatus?.absence) && !physicianStatus?.absence.allowNotifications
  );

  const handleTrackingStatusBar = () => {
    trackCustomEvent({
      eventCategory: 'user interactions',
      eventAction: 'open',
      eventName: 'Arzt_Hinweis_bar | patient',
    });
  };

  const requestSnippets = useMemo(() => {
    return quickReplySnippets?.filter(({ tags = [] }) =>
      tags.some(snippetTag => chat?.reference?.data?.tags?.some(({ id }) => snippetTag.id === id)),
    );
  }, [quickReplySnippets, chat?.reference?.data?.tags]);

  const composerAreaActionItems = useMemo(
    () =>
      [
        isPamSession &&
          simpleprax?.active &&
          !isPatientUserId(userProfile.id) &&
          chat?.reference?.data?.contactDetails?.userId && {
            text: t('global:actions.startQuestionnaire'),
            icon: <DocumentIcon className="w-text h-text text-blue-500" />,
            onClick: () =>
              open({
                modal: MODALS.NEW_QUESTIONNAIRE,
                userId:
                  chat?.reference?.data?.contactDetails?.representedPerson?.userId ||
                  chat?.reference?.data?.contactDetails?.userId,
                tab: 'groups',
                sessionId: id,
              }),
          },
      ].filter(Boolean),
    [
      isPamSession,
      simpleprax?.active,
      userProfile.id,
      chat?.reference?.data?.contactDetails,
      t,
      open,
      id,
    ],
  );

  return (
    <div
      data-component="SessionChatModule"
      className="@container flex flex-col w-full overflow-hidden h-full bg-slate-100"
      ref={dropTarget}
    >
      {config.CLIENT_TYPE !== CLIENT_TYPES.CHAT &&
        (!isPamSession || isPatientUserId(userProfile.id)) && (
          <ChatTitleBarModule
            name={
              isPamSession && chat?.reference ? (
                <div>
                  <div className="flex items-center space-x-2">
                    <div className="truncate">{getInquiryTitle(chat?.reference, t)}</div>
                    {chat?.reference?.data?.source === Sources.PHONE &&
                      chat?.reference?.data?.phoneStatus !== PhoneStatus.COMPLETED && (
                        <PhoneStatusBadge
                          phoneStatus={chat?.reference?.data?.phoneStatus}
                          variant={Variants.ICON}
                        />
                      )}
                    {chat?.reference?.data?.status === RequestStatus.CLOSED && (
                      <InquiryStatusLabel
                        status={RequestStatus.CLOSED}
                        variant={InquiryStatusLabelVariants.TEXT}
                      />
                    )}
                  </div>
                  {chat?.reference?.data?.contactDetails?.representedPerson && (
                    <div className="text-slate-600 text-sm font-normal">
                      {t('global:address_book.inquiries.representedPersonSubtitle', {
                        name: getFullNameWithFL(
                          findContactByUserId(
                            chat?.reference?.data?.contactDetails?.representedPerson.userId,
                          )?.user || chat?.reference?.data?.contactDetails?.representedPerson,
                        ),
                      })}
                    </div>
                  )}
                </div>
              ) : (
                title
              )
            }
            id={id}
            chatMessagesExport={chatMessagesExport}
            canCall={isInternal1On1 && isModerator}
            actionItems={titleActionItems}
          />
        )}
      {physicianStatus?.status && (
        <PhysicianStatusBar
          text={physicianStatus.status}
          detailsUrl={getQueryString({ modal: MODALS.PHYSICIAN_STATUS_MODAL })}
          onClick={handleTrackingStatusBar}
        />
      )}
      <div
        className={`flex flex-col border-t border-t-slate-200 flex-auto ${showRequestInfoBanner ? 'mb-5' : ''}`}
      >
        {hasInactiveUsers && !isPamSession && (
          <InactiveChatRemark>
            {isInternal1On1
              ? t('messenger:PhysicianChatRoomModule.inactiveUser', {
                  user: otherParticipants[0].fullNameWithTitle,
                })
              : t('messenger:PhysicianChatRoomModule.inactiveUsers')}
          </InactiveChatRemark>
        )}
        {status.loadMessages === 'succeeded' ? (
          <ChatStream
            messages={enhancedMessages}
            newMessagesLabel={t('global:ChatMessageStream.separator.new_messages')}
            classNames="px-4 pt-4 @lg:px-12 @lg:pt-12"
            autoResponseMessage={chat?.autoResponseMessage}
            header={
              isPamSession ? null : (
                <SessionChatStreamHeader
                  title={title}
                  initials={chat && isInternal1On1 ? otherParticipants[0].initials : null}
                  subtitle={
                    !isInternal1On1 && (
                      <Button kind="link" onClick={handleShowDetail}>
                        {t(
                          `SessionChatModule.${
                            isModerator || isOrganizer ? 'organizeChat' : 'showParticipants'
                          }`,
                        )}
                      </Button>
                    )
                  }
                  className="flex-auto"
                />
              )
            }
            deletionNoteText={
              (chat && chat.expires) || (isPamSession && isPatientUserId(userProfile.id))
                ? t('SessionChatModule.deletion_note')
                : null
            }
            deletionNoteUrl={
              (chat && chat.expires) || (isPamSession && isPatientUserId(userProfile.id))
                ? t('SessionChatModule.deletion_note_url')
                : null
            }
            messageSeenText={
              chat && chat.participants.length > 2 && !isPracticeToSinglePatientChat
                ? t('messenger:message_seen.multi')
                : t('messenger:message_seen.single')
            }
            lastReadMessageId={chat && chat.messageLastReadId}
            fileMessageClickHandler={fileMessageClickHandler}
            fileMessageDownloadHandler={fileMessageDownloadHandler}
            // eslint-disable-next-line react/jsx-no-bind
            audioSourceResolver={fetchAudioSource}
            deletedMessageText={t('SessionChatModule.messageDeleted')}
            // eslint-disable-next-line react/no-unstable-nested-components
            MessageActionMenu={() => (
              <SessionChatMessageContextMenu
                downloadFile={downloadFile}
                onToggleImportant={toggleMessageImportant}
                onMarkedAsUnread={handleMarkedAsUnread}
                onDelete={handleDeleteMessage}
                onWriteExamination={canWriteExamination ? onWriteExamination : undefined}
                isPamSession={isPamSession}
              />
            )}
            isUserPatient={userProfile.flags.isPatient}
            anchor={location.hash.slice(1)}
          />
        ) : (
          <div className="flex flex-1 justify-center items-center">
            <Spinner size="3xl" />
          </div>
        )}
      </div>
      {showRequestInfoBanner && (
        <RequestInfoBanner
          onClick={handleOpenMessageComposer}
          disabled={!requestCanBeReopened}
          requestStatus={chat?.reference?.data?.status}
          phoneStatus={chat?.reference?.data?.phoneStatus}
          isForwardedCall={chat?.reference?.data?.isForwardedCall}
        />
      )}
      {physicianStatus?.absence?.note && (
        <AbsenceInfoBanner
          className="p-5"
          startAt={physicianStatus.absence.startAt}
          endAt={physicianStatus.absence.endAt}
        >
          {physicianStatus.absence.note}
        </AbsenceInfoBanner>
      )}
      {chat?.type === SESSION_TYPES.PAM_CHAT && (
        <SessionChatModuleQuickReply
          containerRef={quickReplyRef}
          snippets={requestSnippets}
          mode={quickReplyMode}
          onClick={handleQuickReplyClick}
        />
      )}
      <div className={`${!showMessageComposer() && 'hidden'}`}>
        <ComposerAreaModule
          id={id}
          sendMessage={send}
          snippets={snippets}
          isSendingMessage={status.send && status.send === 'loading'}
          errors={status.send && status.send === 'failed'}
          dropTarget={dropTarget}
          chatCharacters={chat && isPatientsOnlySession(chat.participants) ? chatCharacters : null}
          preventAudioRecording={config.CLIENT_TYPE === CLIENT_TYPES.CHAT}
          isPamSession={isPamSession}
          selectedSnippet={selectedQuickReplySnippet}
          actionItems={composerAreaActionItems}
        />
      </div>
      {showPatientWidetActions && requestCanBeReopened && (
        <div className="bg-white w-full flex flex-col lg:items-center gap-3 p-4 border-t-1 border-gray-400">
          <div className="font-semibold text-lg text-gray-600">
            {t('SessionChatModule.newRequest')}
          </div>
          <PatientWidgetAction
            chatId={chat?.reference?.data?.chatId}
            physicianUserId={physicianUserId}
          />
        </div>
      )}
    </div>
  );
};

SessionChatModule.propTypes = {
  id: PropTypes.string.isRequired,
  chatCharacters: FEATURE_PROP_TYPE,
  participantId: PropTypes.string,
  tan: PropTypes.string,
};

export default SessionChatModule;
